import { useState } from "react";
import {
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Group,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { IconArrowUpRight, IconRefresh } from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { humanize } from "@/lib/format";
import { entityListPath, entityPath } from "@/routes/paths";
import { DashboardChart } from "./dashboard/DashboardChart";
import { PlatformBalances } from "./dashboard/PlatformBalances";
import {
  dashboardWindow,
  inWindow,
  money,
  onboarding,
  receipts,
  sourceProblem,
  stateCounts,
  sumMoney,
  timeBuckets,
  timeLabel,
} from "./dashboard/metrics";
import type { Period, Window } from "./dashboard/metrics";
import { useDashboard, useDashboardClock, usePageVisible } from "./dashboard/useDashboard";
import type { DashboardSources } from "./dashboard/useDashboard";
import styles from "./dashboard/dashboard.module.css";

function Problem({ message }: { message: string | null }) {
  return (
    <div role="status" className={styles.sourceNote}>
      {message}
    </div>
  );
}

function Stat({
  label,
  value,
  note,
  to,
  problem,
}: {
  label: string;
  value: string;
  note: string;
  to: string;
  problem: string | null;
}) {
  return (
    <Paper withBorder p="md" radius="lg">
      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          {label}
        </Text>
        <Anchor component={Link} to={to} aria-label={`Explore ${label}`}>
          <IconArrowUpRight size={16} />
        </Anchor>
      </Group>
      {problem ? (
        <Problem message={problem} />
      ) : (
        <>
          <Text className={styles.metric}>{value}</Text>
          <Text size="xs" c="dimmed">
            {note}
          </Text>
        </>
      )}
    </Paper>
  );
}

function PanelTitle({ title, note, to }: { title: string; note: string; to?: string }) {
  return (
    <Group justify="space-between" align="flex-start" mb="md">
      <div>
        <Title order={4}>{title}</Title>
        <Text size="xs" c="dimmed">
          {note}
        </Text>
      </div>
      {to && (
        <Anchor component={Link} to={to} size="xs">
          Explore
        </Anchor>
      )}
    </Group>
  );
}

export function DashboardRecentView({
  sources,
  window,
  zone,
}: {
  sources: DashboardSources;
  window: Window;
  zone: string;
}) {
  const problems = Object.fromEntries(
    Object.entries(sources).map(([key, source]) => [
      key,
      sourceProblem(source, key === "openWithdrawals"),
    ]),
  );
  const cohortProblem = problems.users || problems.kyc;
  const cohort = onboarding(sources.users.data, sources.kyc.data, sources.cards.data, window);
  const received = receipts(sources.activity.data, window);
  const fundingProblem =
    problems.activity ||
    (received.total === null ? "Invalid amount in a receipt. Totals are unavailable." : null);
  const recentUsers = sources.users.data.filter((u) => inWindow(u.createdAt, window));
  const recentWithdrawals = sources.withdrawals.data.filter((w) => inWindow(w.createdAt, window));
  const recentPayments = sources.payments.data.filter((p) => inWindow(p.createdAt, window));
  const open = [...sources.openWithdrawals.data].sort(
    (a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt),
  );
  const lastServer = Object.values(sources)
    .map((s) => s.lastServerSnapshotAt)
    .filter((v): v is number => v !== null);
  const allReady = Object.values(problems).every((p) => !p);
  const failed = Object.values(sources).some((s) => s.error);
  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <div>
          <Title order={3}>Recent activity</Title>
          <Text size="xs" c="dimmed">
            {timeLabel(window.start, zone)} → {timeLabel(window.end, zone)} · first and last chart
            buckets may be partial
          </Text>
        </div>
        <Group gap="xs">
          <Badge color={allReady ? "teal" : "orange"} variant="light">
            {allReady ? "Live subscriptions" : "Connecting / partial data"}
          </Badge>
          {failed && (
            <Button
              size="xs"
              variant="default"
              leftSection={<IconRefresh size={14} />}
              onClick={() => Object.values(sources).forEach((s) => s.refresh())}
            >
              Retry connections
            </Button>
          )}
        </Group>
      </Group>
      <SimpleGrid cols={{ base: 1, xs: 2, lg: 4 }}>
        <Stat
          label="New accounts"
          value={cohort.users.toLocaleString()}
          note="Accounts created in this window"
          to={entityListPath("user")}
          problem={problems.users}
        />
        <Stat
          label="KYC approved"
          value={cohort.approved.toLocaleString()}
          note="New accounts · latest verification"
          to={entityListPath("kycVerification")}
          problem={cohortProblem}
        />
        <Stat
          label="New cardholders"
          value={cohort.cardholders.toLocaleString()}
          note="New accounts · at least one issued card"
          to={entityListPath("card")}
          problem={problems.users || problems.cards}
        />
        <Stat
          label="Onchain received"
          value={`${money(received.total)} USDC`}
          note={`${received.users} recipients · ${received.rows.length} receipts`}
          to={entityListPath("wallet")}
          problem={fundingProblem}
        />
      </SimpleGrid>
      <SimpleGrid cols={{ base: 1, lg: 2 }}>
        <Paper withBorder p="lg" radius="lg">
          <PanelTitle
            title="Account creation"
            note="Accounts by creation time · deleted accounts are excluded"
            to={entityListPath("user")}
          />
          {problems.users ? (
            <Problem message={problems.users} />
          ) : (
            <DashboardChart
              title="Account creation over time"
              buckets={timeBuckets(
                window,
                recentUsers.map((u) => ({ date: u.createdAt })),
              )}
              zone={zone}
            />
          )}
        </Paper>
        <Paper withBorder p="lg" radius="lg">
          <PanelTitle
            title="Incoming USDC"
            note="Onchain receipts by transaction time · volume, not balance or revenue"
          />
          {fundingProblem ? (
            <Problem message={fundingProblem} />
          ) : (
            <DashboardChart
              title="Onchain USDC received over time"
              monetary
              color="teal"
              buckets={timeBuckets(
                window,
                received.rows.map((e) => ({ date: e.occurredAt, amount: e.amountMicroUsdc })),
              )}
              zone={zone}
            />
          )}
        </Paper>
      </SimpleGrid>
      <SimpleGrid cols={{ base: 1, md: 2 }}>
        <Paper withBorder p="lg" radius="lg">
          <PanelTitle
            title="Onboarding progress"
            note="Accounts created in the selected window · current outcomes"
          />
          {cohortProblem || problems.cards ? (
            <Problem message={cohortProblem || problems.cards} />
          ) : (
            <Stack gap="md">
              {[
                ["Accounts created", cohort.users],
                ["KYC started", cohort.started],
                ["KYC approved", cohort.approved],
                ["Card issued", cohort.cardholders],
                ["Digital wallet linked", cohort.digitalWallets],
              ].map(([label, count]) => (
                <div key={String(label)}>
                  <Group justify="space-between" mb={5}>
                    <Text size="sm">{label}</Text>
                    <Text size="sm" fw={600}>
                      {count}{" "}
                      <Text span size="xs" c="dimmed">
                        {cohort.users
                          ? `· ${Math.round((Number(count) / cohort.users) * 100)}%`
                          : ""}
                      </Text>
                    </Text>
                  </Group>
                  <div
                    className={styles.statusTrack}
                    style={{ width: `${cohort.users ? (Number(count) / cohort.users) * 100 : 0}%` }}
                  />
                </div>
              ))}
              <Text size="xs" c="dimmed">
                Unique users at each milestone. Latest KYC outcomes can change on a retry; these are
                not historical conversion rates.
              </Text>
            </Stack>
          )}
        </Paper>
        <Paper withBorder p="lg" radius="lg">
          <PanelTitle
            title="KYC outcomes"
            note="One latest verification per new account, including retries"
            to={entityListPath("kycVerification")}
          />
          {cohortProblem ? (
            <Problem message={cohortProblem} />
          ) : (
            <Stack gap="xs">
              {Object.entries(cohort.states)
                .sort((a, b) => b[1] - a[1])
                .map(([state, count]) => (
                  <Group key={state} justify="space-between">
                    <Text size="sm">
                      {state === "noVerification" ? "No verification" : humanize(state)}
                    </Text>
                    <Text fw={600} size="sm">
                      {count}
                    </Text>
                  </Group>
                ))}
              <Alert variant="light" color="gray" mt="sm">
                {cohort.regionRejected} rejected for unsupported region. Retry requests and API
                error rates require backend event/log reporting.
              </Alert>
            </Stack>
          )}
        </Paper>
      </SimpleGrid>
      <SimpleGrid cols={{ base: 1, md: 2 }}>
        <Paper withBorder p="lg" radius="lg">
          <PanelTitle
            title="Withdrawal requests"
            note="Created in this window · current status and source USDC"
            to={entityListPath("withdrawal")}
          />
          {problems.withdrawals ? (
            <Problem message={problems.withdrawals} />
          ) : recentWithdrawals.length === 0 ? (
            <Text size="sm" c="dimmed">
              No withdrawal requests in this window.
            </Text>
          ) : (
            <Stack gap="sm">
              {stateCounts(recentWithdrawals).map(([state, count]) => (
                <Group key={state} justify="space-between">
                  <StatusBadge state={state} />
                  <Text size="sm">
                    {count} ·{" "}
                    {money(
                      sumMoney(
                        recentWithdrawals
                          .filter((w) => w.state === state)
                          .map((w) => w.sourceAmountMicroUsdc),
                      ),
                    )}{" "}
                    USDC
                  </Text>
                </Group>
              ))}
              <Text size="xs" c="dimmed">
                Completed is the current state of these requests, not the amount paid out during
                this window.
              </Text>
            </Stack>
          )}
        </Paper>
        <Paper withBorder p="lg" radius="lg">
          <PanelTitle
            title="Card payments"
            note="Created in this window · current status and USDC amount"
            to={entityListPath("payment")}
          />
          {problems.payments ? (
            <Problem message={problems.payments} />
          ) : recentPayments.length === 0 ? (
            <Text size="sm" c="dimmed">
              No card payments in this window.
            </Text>
          ) : (
            <Stack gap="sm">
              {stateCounts(recentPayments).map(([state, count]) => (
                <Group key={state} justify="space-between">
                  <StatusBadge state={state} />
                  <Text size="sm">
                    {count} ·{" "}
                    {money(
                      sumMoney(
                        recentPayments
                          .filter((p) => p.state === state)
                          .map((p) => p.amountMicroUsdc),
                      ),
                    )}{" "}
                    USDC
                  </Text>
                </Group>
              ))}
              <Text size="xs" c="dimmed">
                Approved means authorized. Only captured payments represent captured spend.
              </Text>
            </Stack>
          )}
        </Paper>
      </SimpleGrid>
      <Paper withBorder p="lg" radius="lg">
        <PanelTitle
          title="Open withdrawals"
          note="All ages · oldest first · backend state, not a fresh provider check"
          to={entityListPath("withdrawal")}
        />
        {problems.openWithdrawals ? (
          <Problem message={problems.openWithdrawals} />
        ) : (
          <>
            <Text fw={600} size="sm" mb="sm">
              {open.length} open · {money(sumMoney(open.map((w) => w.sourceAmountMicroUsdc)))} USDC
              requested
            </Text>
            {open.length ? (
              <Table.ScrollContainer minWidth={650}>
                <Table highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Withdrawal</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Source amount</Table.Th>
                      <Table.Th>Requested ({zone})</Table.Th>
                      <Table.Th>Last update</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {open.slice(0, 10).map((w) => (
                      <Table.Tr key={w.id}>
                        <Table.Td>
                          <Anchor component={Link} to={entityPath("withdrawal", w.id)} size="sm">
                            {w.id.slice(0, 8)}…
                          </Anchor>
                        </Table.Td>
                        <Table.Td>
                          <StatusBadge state={w.state} />
                        </Table.Td>
                        <Table.Td>{money(sumMoney([w.sourceAmountMicroUsdc]))} USDC</Table.Td>
                        <Table.Td>{timeLabel(Date.parse(w.createdAt), zone)}</Table.Td>
                        <Table.Td>{timeLabel(Date.parse(w.updatedAt), zone)}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            ) : (
              <Text size="sm" c="dimmed">
                No withdrawals currently initiated, burning, or processing.
              </Text>
            )}
            {open.length > 10 && (
              <Text size="xs" c="dimmed" mt="sm">
                Showing the 10 oldest of {open.length}. Explore the withdrawals list for more.
              </Text>
            )}
          </>
        )}
      </Paper>
      <Text size="xs" c="dimmed">
        Business activity from live projections. This does not measure sessions, online users, or
        country. Deleted accounts and erased projections are excluded.{" "}
        {lastServer.length
          ? `Latest server delivery ${timeLabel(Math.max(...lastServer), zone)}.`
          : "Waiting for server data."}
      </Text>
    </Stack>
  );
}

function RecentDashboard({
  period,
  zone,
  now,
  visible,
}: {
  period: Period;
  zone: string;
  now: number;
  visible: boolean;
}) {
  const window = dashboardWindow(period, now, zone);
  // A stable lower bound keeps the live subscription open while the clock advances.
  // Parent remounts at local midnight and when controls change, bounding long-lived reads.
  const [queryStart] = useState(window.start);
  const sources = useDashboard(queryStart, visible);
  return visible ? (
    <DashboardRecentView sources={sources} window={window} zone={zone} />
  ) : (
    <Alert>Live subscriptions are suspended while this tab is hidden.</Alert>
  );
}

export function DashboardPage() {
  const [period, setPeriod] = useState<Period>("7d");
  const [zone, setZone] = useState("Europe/Paris");
  const now = useDashboardClock();
  const visible = usePageVisible();
  const day = new Intl.DateTimeFormat("en-CA", {
    timeZone: zone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
  return (
    <Box className={styles.page}>
      <PageHeader title="Dashboard" subtitle="Customer assets, onboarding, and money movement." />
      <Stack gap="xl">
        <PlatformBalances zone={zone} now={now} visible={visible} />
        <Group justify="space-between">
          <Text size="sm" fw={600}>
            Activity window
          </Text>
          <Group gap="sm">
            <Select
              aria-label="Activity window"
              value={period}
              onChange={(v) => v && setPeriod(v as Period)}
              allowDeselect={false}
              data={[
                { value: "today", label: "Today" },
                { value: "24h", label: "Last 24 hours" },
                { value: "7d", label: "Last 7 days" },
              ]}
              w={170}
            />
            <Select
              aria-label="Timezone"
              value={zone}
              onChange={(v) => v && setZone(v)}
              allowDeselect={false}
              data={["Europe/Paris", "UTC", "Asia/Shanghai"]}
              w={180}
            />
          </Group>
        </Group>
        <RecentDashboard
          key={`${period}:${zone}:${day}`}
          period={period}
          zone={zone}
          now={now}
          visible={visible}
        />
      </Stack>
    </Box>
  );
}
